import { z } from 'zod';
import {
  DEFAULT_GENERATION_MODEL_KEY,
  LANDING_PAGE_URL_MAX_LEN,
  OPTIONAL_GUIDANCE_MAX_BYTES,
  PROMPT_INJECTION_PATTERN,
  RUN_MODES,
  RUN_STATUSES,
  SOURCE_COPY_MAX_BYTES,
  STRUCTURED_PREMISE_MARKDOWN_MAX_BYTES,
  STRUCTURED_PREMISE_PACKET_COUNT,
  STRUCTURED_PREMISE_TEXT_MAX_BYTES,
  WINNING_STRUCTURE_MAX_BYTES,
} from '../constants.js';
import {
  GenerationModelKeySchema,
  SafeGenerationModelMetadataSchema,
  getGenerationModelMetadata,
} from './generation-models.js';

// ─── Entry-or-free-text union ─────────────────────────────────────────────────

// Shared constraint for both free-text branches (value + ephemeralValue).
// Includes the same injection-pattern refine used by optionalGuidance /
// winningStructure so user-supplied text can't smuggle prompt directives.
const freeTextValueSchema = z
  .string()
  .min(1)
  .max(200)
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

// A brief field can either:
//   1. reference an existing library entry by ID (creates a parameterPin row),
//   2. supply new free-text (API auto-creates a library entry + pin), or
//   3. supply an AI-suggested ephemeral value (no library entry, no pin row).
export const EntryOrFreeTextSchema = z.union([
  z.object({ entryId: z.string().uuid() }),
  z.object({ value: freeTextValueSchema }),
  z.object({ ephemeralValue: freeTextValueSchema }),
]);
export type EntryOrFreeText = z.infer<typeof EntryOrFreeTextSchema>;

const landingPageUrlValueSchema = z
  .string()
  .min(1)
  .max(LANDING_PAGE_URL_MAX_LEN)
  .url()
  .refine((s) => {
    try {
      const parsed = new URL(s);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }, 'landing_page_url_invalid_url')
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

export const LandingPageUrlFieldSchema = z.union([
  z.object({ entryId: z.string().uuid() }),
  z.object({ value: landingPageUrlValueSchema }),
  z.object({ ephemeralValue: landingPageUrlValueSchema }),
]);
export type LandingPageUrlField = z.infer<typeof LandingPageUrlFieldSchema>;

// ─── Injection-safe free-form text fields ─────────────────────────────────────

const optionalGuidanceSchema = z
  .string()
  .max(OPTIONAL_GUIDANCE_MAX_BYTES, 'optionalGuidance exceeds 4KB')
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

const winningStructureSchema = z
  .string()
  .max(WINNING_STRUCTURE_MAX_BYTES, 'winningStructure exceeds 64KB')
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

const requiredWinningStructureSchema = z
  .string()
  .trim()
  .min(1)
  .max(WINNING_STRUCTURE_MAX_BYTES, 'winningStructure exceeds 64KB')
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

const sourceCopySchema = z
  .string()
  .min(1)
  .refine((s) => s.trim().length > 0)
  .refine(
    (s) => new TextEncoder().encode(s).byteLength <= SOURCE_COPY_MAX_BYTES,
    'sourceCopy exceeds 64KB'
  )
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

export const RunModeSchema = z.enum(RUN_MODES);

const structuredPremiseTextSchema = z
  .string()
  .trim()
  .min(1)
  .max(STRUCTURED_PREMISE_TEXT_MAX_BYTES)
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

const structuredPremiseMarkdownSchema = z
  .string()
  .trim()
  .min(1)
  .max(STRUCTURED_PREMISE_MARKDOWN_MAX_BYTES)
  .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern');

export const StructuredPremisePacketSchema = z.object({
  rank: z.number().int().min(1).max(STRUCTURED_PREMISE_PACKET_COUNT),
  title: structuredPremiseTextSchema,
  hook: structuredPremiseTextSchema,
  synopsis: structuredPremiseTextSchema,
  riskToAvoid: structuredPremiseTextSchema,
  displayMarkdown: structuredPremiseMarkdownSchema,
});
export type StructuredPremisePacket = z.infer<typeof StructuredPremisePacketSchema>;

export const StructuredPremisePacketsSchema = z
  .array(StructuredPremisePacketSchema)
  .length(STRUCTURED_PREMISE_PACKET_COUNT)
  .superRefine((packets, ctx) => {
    const seenRanks = new Set<number>();
    for (const [index, packet] of packets.entries()) {
      if (seenRanks.has(packet.rank)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'duplicate_premise_rank',
          path: [index, 'rank'],
        });
      }
      seenRanks.add(packet.rank);
    }
  });
export type StructuredPremisePackets = z.infer<typeof StructuredPremisePacketsSchema>;

export const RunStructuredOutputSchema = z.object({
  premisePackets: StructuredPremisePacketsSchema,
});
export type RunStructuredOutput = z.infer<typeof RunStructuredOutputSchema>;

// ─── Request schemas ──────────────────────────────────────────────────────────

const BaseCreateRunRequestSchema = z.object({
  productId: z.string().min(1),
  productVersion: z.number().int().positive().optional(),
  generationModelKey: GenerationModelKeySchema.optional(),
  market: z.string().min(2).max(80),
  angle: EntryOrFreeTextSchema,
  avatar: EntryOrFreeTextSchema,
  problemMechanism: EntryOrFreeTextSchema,
  solutionMechanism: EntryOrFreeTextSchema,
  ingredients: z.array(EntryOrFreeTextSchema).min(1),
  awarenessLevel: EntryOrFreeTextSchema,
  landingPageUrl: LandingPageUrlFieldSchema.optional(),
  optionalGuidance: optionalGuidanceSchema.optional(),
});

const ExistingStructureCreateRunRequestSchema = BaseCreateRunRequestSchema.extend({
  mode: z.literal('use_existing_structure'),
  winningStructure: requiredWinningStructureSchema,
});

const ScratchPremiseSetCreateRunRequestSchema = BaseCreateRunRequestSchema.extend({
  mode: z.literal('scratch_premise_set'),
});

const ScratchAdDraftCreateRunRequestSchema = BaseCreateRunRequestSchema.extend({
  mode: z.literal('scratch_ad_draft'),
  parentPremiseRunId: z.string().uuid(),
  selectedPremise: StructuredPremisePacketSchema,
});

const FbAdToAdvertorialCreateRunRequestSchema = BaseCreateRunRequestSchema.extend({
  mode: z.literal('fb_ad_to_advertorial'),
  sourceCopy: sourceCopySchema,
});

const CompetitorAdvertorialToAdvertorialCreateRunRequestSchema = BaseCreateRunRequestSchema.extend({
  mode: z.literal('competitor_advertorial_to_advertorial'),
  sourceCopy: sourceCopySchema,
});

const CreateRunRequestWithModeSchema = z.discriminatedUnion('mode', [
  ExistingStructureCreateRunRequestSchema,
  ScratchPremiseSetCreateRunRequestSchema,
  ScratchAdDraftCreateRunRequestSchema,
  FbAdToAdvertorialCreateRunRequestSchema,
  CompetitorAdvertorialToAdvertorialCreateRunRequestSchema,
]);

export const CreateRunRequestSchema = z.preprocess((input) => {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const record = input as Record<string, unknown>;

    return {
      ...record,
      ...(!('mode' in record) ? { mode: 'use_existing_structure' } : {}),
      ...(record.generationModelKey === undefined
        ? { generationModelKey: DEFAULT_GENERATION_MODEL_KEY }
        : {}),
    };
  }

  return input;
}, CreateRunRequestWithModeSchema);
export type CreateRunRequest = z.infer<typeof CreateRunRequestSchema>;

export const UpdateRunStatusRequestSchema = z.object({
  status: z.enum(RUN_STATUSES),
});
export type UpdateRunStatusRequest = z.infer<typeof UpdateRunStatusRequestSchema>;

export const ListRunsQuerySchema = z.object({
  product: z.string().optional(),
  market: z.string().optional(),
  status: z.enum(RUN_STATUSES).optional(),
  userId: z.string().uuid().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  q: z.string().optional(),
  limit: z.coerce.number().int().positive().max(200).default(50),
  offset: z.coerce.number().int().nonnegative().default(0),
});
export type ListRunsQuery = z.infer<typeof ListRunsQuerySchema>;

// ─── Response schemas ─────────────────────────────────────────────────────────

export const RunParameterPinSchema = z.object({
  parameterType: z.string(),
  parameterEntryId: z.string().uuid(),
  parameterEntryVersion: z.number().int().positive(),
  displayOrder: z.number().int().nonnegative(),
});
export type RunParameterPin = z.infer<typeof RunParameterPinSchema>;

const RunResponseObjectSchema = z.object({
  id: z.string().uuid(),
  /** Short human-readable run ID, formatted as `V{displayNumber}` (V1, V2, …). */
  displayId: z.string(),
  displayNumber: z.number().int().nonnegative(),
  userId: z.string().uuid(),
  productId: z.string(),
  productVersion: z.number().int().positive(),
  market: z.string(),
  angle: z.string(),
  avatar: z.string(),
  problemMechanism: z.string(),
  solutionMechanism: z.string(),
  awarenessLevel: z.string(),
  /**
   * Denormalized ingredient display names in slot order (index = displayOrder).
   * Ingredients backed by a library entry are present here AND in parameterPins;
   * ephemeral ingredients appear here only (no pin row exists for them).
   * Used by the replay path to reconstruct the full ingredient list including
   * any AI-suggested ephemeral values.
   */
  ingredients: z.array(z.string()),
  landingPageUrl: z.string().url().nullable().optional(),
  optionalGuidance: z.string().nullable(),
  winningStructure: z.string(),
  mode: RunModeSchema.default('use_existing_structure'),
  parentPremiseRunId: z.string().uuid().nullable().default(null),
  selectedPremise: StructuredPremisePacketSchema.nullable().default(null),
  structuredOutput: RunStructuredOutputSchema.nullable().default(null),
  generationModel: SafeGenerationModelMetadataSchema.optional(),
  systemPromptId: z.string().uuid(),
  systemPromptVersion: z.number().int().positive(),
  outputMarkdown: z.string().nullable(),
  status: z.enum(RUN_STATUSES),
  warnings: z.array(z.unknown()),
  responseId: z.string().nullable(),
  inputTokens: z.number().int().nullable(),
  cachedInputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  reasoningTokens: z.number().int().nullable(),
  estimatedCostUsd: z.number().nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  // Optional in the schema: present on /api/runs/:id (single fetch) but
  // omitted from the list endpoint to keep responses small.
  parameterPins: z.array(RunParameterPinSchema).optional(),
});
export const RunResponseSchema = z.preprocess((input) => {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const record = input as Record<string, unknown>;

    return {
      ...record,
      ...(record.generationModel === undefined
        ? { generationModel: getGenerationModelMetadata(DEFAULT_GENERATION_MODEL_KEY) }
        : {}),
    };
  }

  return input;
}, RunResponseObjectSchema);
export type RunResponse = z.infer<typeof RunResponseSchema>;

export const ListRunsResponseSchema = z.object({
  ok: z.literal(true),
  items: z.array(RunResponseSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type ListRunsResponse = z.infer<typeof ListRunsResponseSchema>;
